import React from 'react';
import { useTranslation } from 'react-i18next';
import type { PermissionScope } from '../../hooks/usePermission';
import classes from './ScopeReach.module.css';

interface Props {
  scopes: PermissionScope[];
  value: PermissionScope;
  onChange: (scope: PermissionScope) => void;
  isDisabled?: (scope: PermissionScope) => boolean;
  isReadOnly?: boolean;
  isSensitive?: boolean;
  label: string;
}

/**
 * Охват права как шкала: «Своё → Подчинённые → Вертикаль → Вся компания», выбранный заливает
 * всё до себя — видно, докуда дотягивается право. Цвет — системный зелёный, у чувствительного
 * права — оранжевый, как щит. Анимируется только заливка.
 */
export const ScopeReach: React.FC<Props> = ({
  scopes,
  value,
  onChange,
  isDisabled,
  isReadOnly,
  isSensitive,
  label,
}) => {
  const { t } = useTranslation();
  const index = scopes.indexOf(value);
  const refs = React.useRef<(HTMLButtonElement | null)[]>([]);
  const isOff = (s: PermissionScope) => !!isReadOnly || !!isDisabled?.(s);
  // Как у radiogroup: одна остановка Tab, стрелки выбирают соседний доступный охват.
  const onKeyDown = (e: React.KeyboardEvent) => {
    const step = { ArrowLeft: -1, ArrowUp: -1, ArrowRight: 1, ArrowDown: 1 }[e.key];
    if (!step) return;
    e.preventDefault();
    for (let i = index + step; i >= 0 && i < scopes.length; i += step) {
      if (isOff(scopes[i])) continue;
      onChange(scopes[i]);
      refs.current[i]?.focus();
      return;
    }
  };
  const reach = `${((index + 1) / scopes.length) * 100}%`;
  return (
    <div
      role="radiogroup"
      aria-label={label}
      onKeyDown={onKeyDown}
      className={classes.track}
      data-readonly={isReadOnly || undefined}
      style={
        {
          '--reach-color': `var(--mantine-color-${isSensitive ? 'orange' : 'teal'}-6)`,
        } as React.CSSProperties
      }
    >
      <span className={classes.fill} style={{ width: reach }} aria-hidden />
      <span className={classes.edge} style={{ left: reach }} aria-hidden />
      {scopes.map((s, i) => (
        <button
          key={s}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="button"
          role="radio"
          aria-checked={s === value}
          tabIndex={s === value ? 0 : -1}
          data-reached={i <= index || undefined}
          className={classes.step}
          disabled={isOff(s)}
          onClick={() => onChange(s)}
        >
          {t(`roles.scopes.${s}`)}
        </button>
      ))}
    </div>
  );
};
